import dayjs from "dayjs";
import { array, date, number, object, ref, string } from "yup";

import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import {
  ICampaignTemplate,
  ICampaignTemplateVariables,
} from "@/interfaces/campaign-template.interfaces";
import {
  ICampaign,
  ICampaignInput,
  ISegment,
  ImageData,
  ISchedule,
} from "@/interfaces/campaign.interface";
import { ICampaignSimpleMessage } from "@/interfaces/campaign-simple-message.interface";
import { CampaignFormMessages } from "@/messages/campaigns/campaign-form.messages";
import { CampaignFormat, CampaignStatus, CampaignType } from "@/types/campaign.types";

// Função para gerar códigos de campanha
const CODE_GENERATION = {
  LENGTH: 8,
  CHARACTERS: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
} as const;

function generateCampaignCode(customerId: number): string {
  const chars = CODE_GENERATION.CHARACTERS;
  let randomChars = "";
  for (let i = 0; i < CODE_GENERATION.LENGTH; i++) {
    randomChars += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return randomChars;
}

const getDefaultTemplateText = (type: CampaignType): string => {
  switch (type) {
    case CampaignType.DiscountCoupon:
      return "Olá!\n\nUse o código #CAMPAIGNCODE# para obter #DISCOUNT#% de desconto adicional em toda a sua compra.";
    case CampaignType.PromotionalPrice:
      return "Olá!\n\nUse o código #CAMPAIGNCODE# e acesse o desconto para os produtos desta campanha.";
    default:
      return "";
  }
};

// Garantir que template.variables venha como string (backend exige string)
const normalizeTemplateVariables = (vars: unknown) => {
  if (vars === undefined || vars === null) return null;
  if (typeof vars === "string") return vars;
  try {
    return JSON.stringify(vars);
  } catch (e) {
    try {
      // tentativa simples de remover referências circulares
      const seen = new WeakSet<object>();
      const decirc = (obj: unknown): unknown => {
        if (typeof obj !== "object" || obj === null) return obj;
        if (seen.has(obj as object)) return "[Circular]";
        seen.add(obj as object);
        if (Array.isArray(obj)) {
          return obj.map(decirc);
        }
        const result: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
          result[k] = decirc(v);
        }
        return result;
      };
      return JSON.stringify(decirc(vars));
    } catch {
      return "{}";
    }
  }
};

type TagsType = string | string[] | undefined;

export const buildInitialData = (
  template: ICampaignTemplate | undefined,
  isEditing: boolean,
  campaign?: ICampaign | null,
  customerId?: number
): CampaignFormData => {
  const currentDate = dayjs().format("YYYY-MM-DDTHH:mm:ss");
  const defaultStartDate = dayjs().startOf("day").format("YYYY-MM-DDTHH:mm:ss");
  const tenDaysLater = dayjs().add(10, "day").format("YYYY-MM-DDTHH:mm:ss");

  const parseTags = (tags: TagsType): string[] => {
    if (typeof tags === "string") {
      try {
        const parsedTags: string[] = JSON.parse(tags);
        return parsedTags.map((tag) => tag.replace(/^\[|\]$/g, ""));
      } catch (e) {
        return [];
      }
    }
    return (tags || []).map((tag) => tag.replace(/^\[|\]$/g, ""));
  };

  const disabledField =
    isEditing &&
    (campaign?.status === CampaignStatus.Hold || campaign?.status === CampaignStatus.Completed);

  const generatedCode = customerId && !isEditing ? generateCampaignCode(customerId) : "";

  return {
    id: campaign?.id || 0,
    code: campaign?.code || generatedCode,
    name: campaign?.name || "",
    type: campaign?.type || CampaignType.PromotionalPrice,
    discountPercentage: campaign?.discountPercentage || 0,
    startDate: campaign?.startDate || defaultStartDate,
    endDate: campaign?.endDate || tenDaysLater,
    text:
      campaign?.text ||
      template?.text ||
      (template ? getDefaultTemplateText(CampaignType.PromotionalPrice) : ""),
    status: campaign?.status || CampaignStatus.Active,
    format: campaign?.format || CampaignFormat.Cart,
    items: campaign?.items || [],
    segment: {
      id: campaign?.segment?.id || 0,
      campaignId: campaign?.id || 0,
      birthdayPeriod: campaign?.segment?.birthdayPeriod || null,
      gender: campaign?.segment?.gender || null,
      contactSource: campaign?.segment?.contactSource || null,
      minimumAge: campaign?.segment?.minimumAge || null,
      maximumAge: campaign?.segment?.maximumAge || null,
      cities: parseTags(campaign?.segment?.cities || []),
      contactTag: parseTags(campaign?.segment?.contactTag || []),
      activeOnly: true,
      rowDate: campaign?.segment?.rowDate || currentDate,
    },
    schedules: campaign?.schedules || [],
    campaignImages: campaign?.campaignImages || null,
    template: {
      id: campaign?.template?.id || 0,
      campaignId: campaign?.template?.campaignId || campaign?.id || 0,
      name: campaign?.template?.name || template?.name || "",
      variables: campaign?.template?.variables || template?.variables || null,
      text: campaign?.template?.text || template?.text || "",
      rowDate: campaign?.template?.rowDate || template?.rowDate || currentDate,
    },

    newContactTags: [],
    newCityTags: [],
    newTriggerDaysAndTimes: [],

    removedSchedules: [],
    removedItems: [],
    removedSegments: [],
    disabledField: disabledField,
    isEditing: isEditing,
    imagesToDelete: null,

    isDraftStatus: campaign?.status === CampaignStatus.Draft || false,
    isActiveStatus: campaign?.status === CampaignStatus.Active || false,
    isInactiveStatus: campaign?.status === CampaignStatus.Inactive || false,
    isRejectedStatus: campaign?.status === CampaignStatus.Rejected || false,
    isHoldStatus: campaign?.status === CampaignStatus.Hold || false,
    isConfirmedStatus: campaign?.status === CampaignStatus.Completed || false,
    // Simple Message fields - verificar se simpleMessage é string ou objeto
    simpleMessage:
      typeof campaign?.simpleMessage === "string"
        ? campaign.simpleMessage
        : ((campaign?.simpleMessage as ICampaignSimpleMessage)?.messageText ?? ""),
    hasButton:
      typeof campaign?.simpleMessage === "string"
        ? Boolean(campaign?.hasButton)
        : Boolean((campaign?.simpleMessage as ICampaignSimpleMessage)?.hasActionButton),
    buttonText:
      typeof campaign?.simpleMessage === "string"
        ? ((campaign?.buttonText as string) ?? "")
        : ((campaign?.simpleMessage as ICampaignSimpleMessage)?.buttonText ?? ""),
    buttonLink:
      typeof campaign?.simpleMessage === "string"
        ? ((campaign?.buttonLink as string) ?? "")
        : ((campaign?.simpleMessage as ICampaignSimpleMessage)?.buttonLink ?? ""),

    isImmediateScheduling: isEditing ? false : true,
  };
};

export const buildFormSchema = (step: number, messages: CampaignFormMessages) =>
  object().shape({
    ...(step === 0 && {
      // Validações para o primeiro passo (Dados da Campanha)
      name: string().required(messages.errorRequiredCampaignName),
      code: string().required(messages.errorRequiredCampaignCode),
      discountPercentage: number()
        .min(0, messages.errorMinDiscountPercentage)
        .max(100, messages.errorMaxDiscountPercentage),
      startDate: date().required(messages.errorRequiredStartDate),
      endDate: date()
        .min(ref("startDate"), messages.errorEndDateBeforeStartDate)
        .required(messages.errorRequiredEndDate),
    }),
    ...(step === 1 && {
      segment: object().shape({
        minimumAge: number()
          .nullable()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .min(0, messages.errorMinAgeNegative)
          .when("maximumAge", {
            is: (value: number | null) => value !== null && value !== undefined,
            then: (schema) => schema.required(messages.errorRequiredMinimumAge),
          }),
      }),
    }),
    ...(step === 2 && {
      // Validações para o terceiro passo (Produtos ou Mensagem Simples)
      simpleMessage: string().when("type", {
        is: CampaignType.SimpleMessage,
        then: (schema) =>
          schema
            .required(messages.simpleMessageError)
            .max(1024, messages.simpleMessageMaxLength)
            .test(
              "not-starts-with-variable",
              messages.simpleMessageStartsWithVariableError,
              (value) => {
                if (!value) return true; // Se não há valor, a validação required já trata
                const trimmedValue = value.trim();
                // Verifica se o texto começa com uma variável no formato #Variable#
                return !trimmedValue.match(/^#[A-Za-z0-9_]+#/);
              }
            ),
        otherwise: (schema) => schema.optional(),
      }),
      buttonText: string().when(["hasButton", "type"], {
        is: (hasButton: boolean, type: CampaignType) =>
          hasButton && type === CampaignType.SimpleMessage,
        then: (schema) =>
          schema.required(messages.buttonTextError).max(20, messages.buttonTextMaxLength),
        otherwise: (schema) => schema.optional(),
      }),
      buttonLink: string().when(["hasButton", "type"], {
        is: (hasButton: boolean, type: CampaignType) =>
          hasButton && type === CampaignType.SimpleMessage,
        then: (schema) =>
          schema.test("valid-url-if-provided", messages.buttonLinkInvalidError, (value) => {
            if (!value || value.trim().length === 0) return true;
            return /^https?:\/\/.+/.test(value);
          }),
        otherwise: (schema) => schema.optional(),
      }),
    }),
  });

export const normalizeCreationData = (formData: CampaignFormData): ICampaignInput => {
  const { segment, schedules } = formData;

  const segmentData = segment as ISegment;

  // Normalizando contactTag
  const contactTagsArray = Array.isArray(segmentData?.contactTag) ? segmentData.contactTag : [];
  const normalizedTags =
    contactTagsArray.length > 0 ? JSON.stringify(contactTagsArray.map((tag) => `[${tag}]`)) : null;

  // Normalizando cities
  const citiesArray = Array.isArray(segmentData?.cities) ? segmentData.cities : [];
  const normalizedCities =
    citiesArray.length > 0 ? JSON.stringify(citiesArray.map((city) => `[${city}]`)) : null;

  // Normalizando maximumAge e minimumAge
  const maximumAge = segmentData?.maximumAge === "" ? null : segmentData?.maximumAge;
  const minimumAge = segmentData?.minimumAge === "" ? null : segmentData?.minimumAge;

  let schedulesArray = Array.isArray(schedules)
    ? schedules.map((s: { date: string; sent?: boolean }) => ({
        date: s.date,
        sent: Boolean(s.sent),
      }))
    : [];

  if (formData.isImmediateScheduling) {
    const immediateTime = dayjs().add(5, "minute").format("YYYY-MM-DDTHH:mm:ss");
    schedulesArray = [{ date: immediateTime, sent: false }];
  }

  // Remover campos que não fazem parte da interface ICampaignInput
  const {
    newContactTags,
    newCityTags,
    newTriggerDaysAndTimes,
    removedSchedules,
    removedItems,
    removedSegments,
    isEditing,
    disabledField,
    isDraftStatus,
    isActiveStatus,
    isInactiveStatus,
    isRejectedStatus,
    isHoldStatus,
    isConfirmedStatus,
    imagesToDelete,
    // Remover campos de Simple Message do payload principal
    simpleMessage,
    hasButton,
    buttonText,
    buttonLink,
    isImmediateScheduling,
    ...campaignData
  } = formData;

  const normalizedData: ICampaignInput = {
    ...campaignData,
    segment: {
      id: segmentData?.id || 0,
      campaignId: segmentData?.campaignId || 0,
      birthdayPeriod: segmentData?.birthdayPeriod || null,
      gender: segmentData?.gender || null,
      contactSource: segmentData?.contactSource || null,
      contactTag: normalizedTags,
      cities: normalizedCities,
      maximumAge,
      minimumAge,
      activeOnly: true,
      rowDate: segmentData?.rowDate || new Date().toISOString(),
    },
    // Garantir items válidos (originalPrice obrigatório no backend)
    items: Array.isArray(campaignData.items)
      ? campaignData.items.map((it: Record<string, unknown>) => ({
          productId: Number(it.productId || 0),
          productCode: String(it.productCode || ""),
          productName: String(it.productName || ""),
          productDescription: String(it.productDescription || ""),
          minimumQuantity: Number(it.minimumQuantity ?? 1),
          originalPrice: Number(it.originalPrice ?? it.promoPrice ?? 0),
          promoPrice: Number(it.promoPrice ?? 0),
          rowDate: String(it.rowDate || new Date().toISOString()),
        }))
      : [],
    schedules: schedulesArray,
    template: {
      id: formData.template?.id || 0,
      campaignId: formData.template?.campaignId || 0,
      name: formData.template?.name || "",
      // backend exige string em variables
      variables: normalizeTemplateVariables(
        formData.template?.variables || ({} as ICampaignTemplateVariables)
      ) as unknown as ICampaignTemplateVariables,
      text: formData.template?.text || "",
      rowDate: formData.template?.rowDate || new Date().toISOString(),
    },
  };

  // Para Mensagem Simples, omitir items do payload para evitar validação de itens obrigatórios no backend
  if (campaignData.type === CampaignType.SimpleMessage) {
    // Remover completamente items para evitar validação lado servidor
    delete (normalizedData as unknown as Record<string, unknown>).items;
  }

  return normalizedData;
};

export const normalizeUpdateData = (formData: CampaignFormData): ICampaignInput => {
  const { segment, schedules } = formData;

  const segmentData = segment as ISegment;

  // Normalizando contactTag
  const contactTagsArray = Array.isArray(segmentData?.contactTag) ? segmentData.contactTag : [];
  const normalizedTags =
    contactTagsArray.length > 0 ? JSON.stringify(contactTagsArray.map((tag) => `[${tag}]`)) : null;

  // Normalizando cities
  const citiesArray = Array.isArray(segmentData?.cities) ? segmentData.cities : [];
  const normalizedCities =
    citiesArray.length > 0 ? JSON.stringify(citiesArray.map((city) => `[${city}]`)) : null;

  // Normalizando maximumAge e minimumAge
  const maximumAge = segmentData?.maximumAge === "" ? null : segmentData?.maximumAge;
  const minimumAge = segmentData?.minimumAge === "" ? null : segmentData?.minimumAge;

  let schedulesArray: ISchedule[] = Array.isArray(schedules)
    ? schedules.map((s: { id?: number; date: string; sent?: boolean }) => ({
        id: s.id,
        date: s.date,
        sent: Boolean(s.sent),
      }))
    : [];

  if (formData.isImmediateScheduling) {
    const immediateTime = dayjs().add(5, "minute").format("YYYY-MM-DDTHH:mm:ss");
    schedulesArray = [{ date: immediateTime, sent: false }];
  }

  // Remover campos que não fazem parte da interface ICampaignInput
  const {
    newContactTags,
    newCityTags,
    newTriggerDaysAndTimes,
    removedSchedules,
    removedItems,
    removedSegments,
    isEditing,
    disabledField,
    isDraftStatus,
    isActiveStatus,
    isInactiveStatus,
    isRejectedStatus,
    isHoldStatus,
    isConfirmedStatus,
    imagesToDelete,
    // Remover campos de Simple Message do payload principal
    simpleMessage,
    hasButton,
    buttonText,
    buttonLink,
    isImmediateScheduling,
    ...campaignData
  } = formData;

  const normalized: ICampaignInput = {
    ...campaignData,
    segment: {
      id: segmentData?.id || 0,
      campaignId: segmentData?.campaignId || 0,
      birthdayPeriod: segmentData?.birthdayPeriod || null,
      gender: segmentData?.gender || null,
      contactSource: segmentData?.contactSource || null,
      contactTag: normalizedTags,
      cities: normalizedCities,
      maximumAge,
      minimumAge,
      activeOnly: true,
      rowDate: segmentData?.rowDate || new Date().toISOString(),
    },
    items: Array.isArray(campaignData.items)
      ? campaignData.items.map((it: Record<string, unknown>) => ({
          ...(it.id && { id: Number(it.id) }),
          productId: Number(it.productId || 0),
          productCode: String(it.productCode || ""),
          productName: String(it.productName || ""),
          productDescription: String(it.productDescription || ""),
          minimumQuantity: Number(it.minimumQuantity ?? 1),
          originalPrice: Number(it.originalPrice ?? it.promoPrice ?? 0),
          promoPrice: Number(it.promoPrice ?? 0),
          rowDate: String(it.rowDate || new Date().toISOString()),
        }))
      : [],
    schedules: schedulesArray,
    template: {
      id: formData.template?.id || 0,
      campaignId: formData.template?.campaignId || 0,
      name: formData.template?.name || "",
      // backend exige string em variables
      variables: normalizeTemplateVariables(
        formData.template?.variables || ({} as ICampaignTemplateVariables)
      ) as unknown as ICampaignTemplateVariables,
      text: formData.template?.text || "",
      rowDate: formData.template?.rowDate || new Date().toISOString(),
    },
  };

  if (campaignData.type === CampaignType.SimpleMessage) {
    delete (normalized as unknown as Record<string, unknown>).items;
  }

  return normalized;
};

/** Nome do campo com o JSON da campanha no multipart do POST api/v1/campaign (alinhar ao backend se diferente). */
export const CAMPAIGN_CREATE_MULTIPART_JSON_FIELD = "data";

const appendCampaignImageFileToFormData = (
  target: FormData,
  imageData: ImageData,
  options: { campaignId?: string }
) => {
  const fileNameWithExtension = imageData.file.name;
  const rawBaseName = fileNameWithExtension.replace(/\.[^/.]+$/, "");
  const rawExtension = (fileNameWithExtension.split(".").pop() || "").toLowerCase();

  const alphanumericBase = rawBaseName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "")
    .slice(0, 80);

  const fallbackBase = `image${Date.now()}`;
  const sanitizedBaseName = (alphanumericBase || fallbackBase).toLowerCase();
  const sanitizedFullName = `${sanitizedBaseName}.${rawExtension || "png"}`;

  if (options.campaignId !== undefined) {
    target.append("campaignId", options.campaignId);
  }
  target.append("File", imageData.file, sanitizedFullName);
  target.append("extension", rawExtension || "png");
  target.append("name", sanitizedBaseName || "image");
  target.append("logicalAddress", "");
  target.append("phisicalAddress", "");
};

export const buildImageFormData = (imageData: ImageData, campaignId: string) => {
  const formData = new FormData();
  appendCampaignImageFileToFormData(formData, imageData, { campaignId });
  return formData;
};

/** POST api/v1/campaign com imagem: JSON em `data` + mesmos campos de arquivo que api/v1/campaignimage (sem campaignId). */
export const buildCampaignCreateMultipartFormData = (
  campaignPayload: ICampaignInput,
  imageData: ImageData
): FormData => {
  const formData = new FormData();
  formData.append(
    CAMPAIGN_CREATE_MULTIPART_JSON_FIELD,
    JSON.stringify({ ...campaignPayload, campaignImages: null })
  );
  appendCampaignImageFileToFormData(formData, imageData, {});
  return formData;
};

export const getMonthOptions = (messages: CampaignFormMessages) => [
  { label: messages.januaryLabel, value: "01" },
  { label: messages.februaryLabel, value: "02" },
  { label: messages.marchLabel, value: "03" },
  { label: messages.aprilLabel, value: "04" },
  { label: messages.mayLabel, value: "05" },
  { label: messages.juneLabel, value: "06" },
  { label: messages.julyLabel, value: "07" },
  { label: messages.augustLabel, value: "08" },
  { label: messages.septemberLabel, value: "09" },
  { label: messages.octoberLabel, value: "10" },
  { label: messages.novemberLabel, value: "11" },
  { label: messages.decemberLabel, value: "12" },
];

export function normalizeSegmentData(
  segment: ISegment
): Omit<ISegment, "rowDate" | "id" | "campaignId"> {
  const segmentData = segment || ({} as ISegment);

  const contactTagsArray = Array.isArray(segmentData.contactTag) ? segmentData.contactTag : [];
  const normalizedTags =
    contactTagsArray.length > 0 ? JSON.stringify(contactTagsArray.map((tag) => `[${tag}]`)) : null;

  const citiesArray = Array.isArray(segmentData.cities) ? segmentData.cities : [];
  const normalizedCities =
    citiesArray.length > 0 ? JSON.stringify(citiesArray.map((city) => `[${city}]`)) : null;

  const maximumAge = segmentData.maximumAge === "" ? null : segmentData.maximumAge;
  const minimumAge = segmentData.minimumAge === "" ? null : segmentData.minimumAge;

  return {
    birthdayPeriod: segmentData.birthdayPeriod || null,
    gender: segmentData.gender || null,
    contactSource: segmentData.contactSource || null,
    contactTag: normalizedTags,
    cities: normalizedCities,
    maximumAge,
    minimumAge,
    activeOnly: true,
  };
}
